<template>
  <el-dialog
    v-model="modalIsVisible"
    v-loading="loading"
    :width="`${isDesktop ? 50 : 100}%`"
    :show-close="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    @close="close"
  >
    <el-form
      ref="form"
      v-loading="loading"
      :rules="rules"
      :model="payload"
      @submit.prevent
    >
      <el-steps :active="step" align-center>
        <el-step title="Choose file and ID column" />
        <el-step title="Choose type and stream" />
        <el-step title="Choose list" />
      </el-steps>
      <div v-if="step === 0">
        <el-row>
          <el-col :span="24" class="pr-1">
            <vue-csv-import v-model="csv" :fields="columns">
              <vue-csv-input id="file-upload" />
              <label for="file-upload" class="dropzone">
                Click to upload
              </label>
              <vue-csv-map class="styled-table mt-1" />
            </vue-csv-import>
          </el-col>
          <el-col
            :span="24"
            style="display: flex"
            class="flex flex--space-between mt-1"
          >
            <div v-if="csv">
              Spots in column: <b>{{ spotIds.length }}</b>
            </div>
            <div>
              <el-button
                v-if="spotIds.length > 0"
                type="primary"
                size="small"
                round
                @click="next"
              >
                Next
              </el-button>
            </div>
          </el-col>
        </el-row>
      </div>
      <div v-if="step === 1">
        <el-row>
          <el-col :span="12" class="pr-1">
            <el-form-item label="Choose spots type">
              <AdTypeSelect
                v-model="payload.type_id"
                label="Ad type"
                :with-empty="false"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Choose spots stream">
              <StreamSelect
                v-model="payload.stream"
                label="Stream"
                :with-empty="false"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24" class="text-right" style="margin-bottom: 0">
            <el-button type="primary" size="small" round @click="submit">
              Next
            </el-button>
          </el-col>
        </el-row>
      </div>
      <div v-if="step === 2">
        <template v-if="importResult.valid > 0">
          <el-row>
            <el-col :span="24" class="import-info-list">
              <el-form-item>
                <ul>
                  <li>
                    <span>Total: </span>
                    <el-badge :value="importResult.total" type="primary" />
                  </li>
                  <li>
                    <span>Valid: </span>
                    <el-badge :value="importResult.valid" type="success" />
                  </li>
                  <li>
                    <span>Invalid: </span>
                    <el-badge :value="importResult.invalid" type="danger" />
                  </li>
                </ul>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row style="margin: 10px 10px 0 10px">
            <el-col :span="12">
              <el-form-item label="Choose list">
                <el-radio-group
                  v-if="isDesktop"
                  v-model="payload.isNew"
                  size="small"
                >
                  <el-radio-button :value="1">
                    Create new list
                  </el-radio-button>
                  <el-radio-button :value="0"> Choose existed </el-radio-button>
                </el-radio-group>
                <el-select v-else v-model="payload.isNew">
                  <el-option key="1" label="Create new list" :value="1">
                    Create new list
                  </el-option>
                  <el-option key="0" label="Choose existed" :value="0">
                    Choose existed
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item v-if="payload.isNew" prop="name" label="List name">
                <el-input v-model="payload.name" type="text" />
              </el-form-item>
              <el-form-item v-else prop="id" label="Choose spot">
                <SpotListSelect
                  v-model="payload.id"
                  :items="activeLists"
                  :clearable="false"
                />
              </el-form-item>
            </el-col>
            <el-col :span="24" class="text-right">
              <el-form-item style="margin-bottom: 0">
                <el-button type="primary" size="small" round @click="submit">
                  Save
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </template>
        <template v-else>
          <p>
            There are no valid spot rows. Please check your spot list or change
            stream and ad format.
          </p>
          <el-button type="primary" size="small" round @click="next()">
            Choose another file
          </el-button>
          <el-button type="primary" size="small" round @click="prev()">
            Chose another options
          </el-button>
        </template>
      </div>
    </el-form>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue'
import {
  ISpotsListAttributes,
  ISpotsListImportPayload
} from '@/apps/traforama/interfaces/spotsLists'
import { AdType, Stream, SpotsListValidationErrorsKey } from '@/api/types'

import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'
import SpotListSelect from '@/components/Select/SpotListSelect.vue'

import useValidation from '@/hooks/useValidation'
import { ElMessage } from 'element-plus'
import useResize from '@/hooks/useResize'

export default defineComponent({
  name: 'ImportModal',
  components: {
    AdTypeSelect,
    StreamSelect,
    SpotListSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    lists: {
      type: Array,
      default: () => []
    }
  },
  setup(props, context) {
    const spotsListsStore = useSpotsListsStore()
    const { isDesktop } = useResize()
    const loading = ref(false)
    const step = ref(0)
    const form = ref()
    const payload = reactive<ISpotsListImportPayload>({
      id: '',
      name: '',
      spot_ids: [],
      type_id: AdType.Popunder,
      stream: Stream.Adult,
      isNew: 1
    })
    const modalIsVisible = computed({
      get: () => {
        return props.visible
      },
      set: (val) => {
        context.emit('update:modelValue', val)
      }
    })

    const columns = {
      spot_id: {
        required: false,
        label: "Please indicate the spot_id's column"
      }
    }

    const rules = {
      name: { required: true, message: 'Field is required', trigger: 'change' },
      id: { required: true, message: 'Field is required', trigger: 'change' }
    }

    const { provideValidation, setValidationErrors } = useValidation(
      SpotsListValidationErrorsKey
    )
    const errors = provideValidation()

    const close = () => {
      csv.value = []
      step.value = 0
      spotIds.value = []
      context.emit('close')
    }

    const activeLists = computed(() => {
      return props.lists.filter((x: any) => {
        return (
          x.attributes.type_id === payload.type_id &&
          x.attributes.stream === payload.stream
        )
      })
    })

    const importResult = computed(() => {
      return spotsListsStore.importResult
    })

    const submit = () => {
      form.value.validate(async (valid: boolean) => {
        if (valid) {
          try {
            loading.value = true
            const response = await spotsListsStore.ImportItems({ ...payload })
            if (response.id) {
              context.emit('submit')
              close()
            } else {
              next()
            }
          } catch (err: any) {
            setValidationErrors(err, errors)
          } finally {
            loading.value = false
          }
        } else {
          return false
        }
      })
    }

    const next = () => {
      step.value++
      if (step.value > 2) {
        step.value = 0
      }
    }

    const prev = () => {
      step.value--
    }

    const csv = ref()
    const spotIds = ref([])

    watch(csv, (newVal) => {
      if (newVal) {
        spotIds.value = newVal
          .map((i: any) => i.spot_id)
          .filter((i: string) => !isNaN(i) && isFinite(i))
        payload.spot_ids = spotIds.value
      }
    })

    return {
      isDesktop,
      submit,
      rules,
      close,
      form,
      loading,
      step,
      columns,
      next,
      prev,
      payload,
      importResult,
      activeLists,
      csv,
      spotIds,
      modalIsVisible
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(#file-upload) {
  visibility: hidden;
}
.dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
  border: 2px dashed var(--el-color-primary);
  border-radius: 10px;
  background-color: #f8f9fa;
  color: var(--el-color-primary);
  font-size: 16px;
  cursor: pointer;
  transition:
    background-color 0.3s,
    border-color 0.3s;
  text-align: center;
  padding: 20px;
}

.dropzone:hover {
  background-color: #e9ecef;
  border-color: #0056b3;
}

.dropzone:active {
  background-color: #d6d8db;
}

.dropzone::before {
  content: '📂';
  font-size: 40px;
  margin-bottom: 10px;
  display: block;
}

.text-right {
  text-align: right;
}

.el-select {
  width: 100%;
}

.import-info-list {
  .el-form-item {
    margin: 20px 0 0 0;
  }

  text-align: center;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: inline;
    margin-right: 10px;
  }
}

.el-steps--horizontal {
  margin-bottom: 20px;
}

:deep(.el-step__title) {
  font-size: 14px;
}
:deep(.styled-table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 14px;
  color: #606266;

  thead {
    background-color: #f5f7fa;
  }

  th,
  td {
    padding: 12px 16px;
    text-align: left;
  }

  th {
    font-weight: 500;
    color: #909399;
    border-bottom: 1px solid #ebeef5;
  }

  td {
    border-bottom: 1px solid #ebeef5;
  }

  tr:hover {
    background-color: #f5f7fa;
  }

  select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fff;
    color: #606266;
    transition: border-color 0.3s;
  }

  select:focus {
    border-color: #409eff;
    outline: none;
  }

  option {
    padding: 8px 12px;
  }
}
</style>
